import { useCallback, useEffect, useState } from 'react';
import { api, useAuth } from '@onvice/portal-shell-api';

/** Antwort von `GET <apiBase>/acl/me` (Baustein @onvice/module-acl). */
export interface ModuleAclMe {
  enabled: boolean;
  wildcard: boolean;
  userId: string;
  rights: Record<string, string[]>;
  taskRules: Array<{ action: string; owner: boolean; helper: boolean }>;
  source: 'wildcard' | 'grants' | 'legacy';
}

export interface ModuleAclState {
  /** false, bis die Antwort da ist — Knöpfe bis dahin nicht gated rendern (kein Flackern). */
  loaded: boolean;
  enabled: boolean;
  wildcard: boolean;
  /** Darf der Anwender die Zelle? Vor dem Laden: false. */
  can: (docType: string, action: string) => boolean;
  /** Block A für diese Installation (bei Schalter aus: Vorgaben). */
  taskRule: (action: string) => { owner: boolean; helper: boolean } | null;
  refresh: () => void;
}

const inflight = new Map<string, Promise<ModuleAclMe>>();

function load(key: string, apiBase: string): Promise<ModuleAclMe> {
  let p = inflight.get(key);
  if (!p) {
    p = api.get<{ success: boolean; data: ModuleAclMe }>(`${apiBase}/acl/me`).then((r) => r.data);
    p.finally(() => { inflight.delete(key); });
    inflight.set(key, p);
  }
  return p;
}

/**
 * Effektive Modul-ACL-Rechte des angemeldeten Anwenders (E7: der Server liefert sie auch bei
 * Schalter aus aus den heutigen Formeln — EINE Quelle für Knöpfe und Backend).
 * `apiBase` z. B. `/modules/bauberichteV2`. Kein dauerhafter Zwischenspeicher: jeder Einhänger
 * lädt einmal, parallele Aufrufe teilen sich die laufende Anfrage.
 */
export function useModuleAcl(apiBase: string): ModuleAclState {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [me, setMe] = useState<ModuleAclMe | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!userId) { setMe(null); return; }
    let alive = true;
    load(`${apiBase}|${userId}|${tick}`, apiBase)
      .then((d) => { if (alive) setMe(d); })
      .catch(() => { if (alive) setMe({ enabled: false, wildcard: false, userId, rights: {}, taskRules: [], source: 'legacy' }); });
    return () => { alive = false; };
  }, [apiBase, userId, tick]);

  const can = useCallback((docType: string, action: string) => {
    if (!me) return false;
    if (me.wildcard) return true;
    return (me.rights[docType] ?? []).includes(action);
  }, [me]);

  const taskRule = useCallback((action: string) => me?.taskRules.find((r) => r.action === action) ?? null, [me]);

  return {
    loaded: me !== null,
    enabled: me?.enabled ?? false,
    wildcard: me?.wildcard ?? false,
    can,
    taskRule,
    refresh: () => setTick((t) => t + 1),
  };
}
